// 裝置清單「多選批次」操作列（APP#51 後續子項；對標 VistaFlow）。
// 進多選 → 勾選多台 → 本列出現：移至房間 / 共享 / 改名 / 刪除。
// - rename/share 走 API；LS App unbind 走 Signal；move-to-room 是本地偏好。
// - 部分失敗：runBatch 逐台套用、收集哪台失敗，操作後彙整「N 成功、M 失敗 + 失敗台名」，
//   不因單台失敗中止整批（batchOps.ts）。
// - authz：rename/share/delete 為 owner-only，對混合選取只作用可管理台、被擋的共享台明示；
//   全共享選取 → 管理型鈕 disable。move-to-room 是本地偏好、任何成員皆可。
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api';
import type { DeviceSummary } from './deviceModel';
import { deviceName } from './deviceModel';
import { saveRoom, type RoomMap } from './rooms';
import { ConfirmDialog } from './settings/widgets';
import { unbindDevice } from './settings/ops';
import { batchRenameName, partitionSelection, runBatch, type BatchOutcome } from './batchOps';

type Mode = null | 'move-room' | 'share' | 'rename';

export function BatchActionBar({
  devices,
  selectedIds,
  uid,
  roomNames,
  onExit,
  onClearSelection,
  onRoomsChange,
  onReload,
}: {
  devices: DeviceSummary[];
  selectedIds: ReadonlySet<string>;
  uid: string;
  roomNames: string[];
  onExit: () => void;
  onClearSelection: () => void;
  onRoomsChange: (map: RoomMap) => void;
  onReload: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [outcome, setOutcome] = useState<BatchOutcome | null>(null);

  // form drafts
  const [roomDraft, setRoomDraft] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [prefixDraft, setPrefixDraft] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of devices) m.set(d.device_id, deviceName(d));
    return m;
  }, [devices]);

  const { manageableIds, blockedIds } = useMemo(
    () => partitionSelection(devices, selectedIds),
    [devices, selectedIds]
  );
  const selectedCount = selectedIds.size;
  const canManage = manageableIds.length > 0;

  function closePanels() {
    setMode(null);
    setConfirmDelete(false);
    setFormError(null);
    setRoomDraft('');
    setEmailDraft('');
    setPrefixDraft('');
  }

  function finish(next: BatchOutcome) {
    setOutcome(next);
    setBusy(false);
    closePanels();
    // 成功台已套用 → 收合勾選、讓上層重載（delete/share 需要新資料）。
    onClearSelection();
    void onReload();
  }

  // ---- 移至房間（本地偏好；對所有選取台，任何成員皆可）----
  function applyMoveRoom() {
    const room = roomDraft.trim();
    setBusy(true);
    setFormError(null);
    // 逐台寫本地 room map（saveRoom 回傳最新 map，最後一次即完整）。
    let map: RoomMap = {};
    const ids = devices.filter((d) => selectedIds.has(d.device_id)).map((d) => d.device_id);
    for (const id of ids) map = saveRoom(uid, id, room);
    onRoomsChange(map);
    finish({ succeeded: ids, failed: [] });
  }

  // ---- 共享（owner-only；批次邀請同一 email 到每台可管理台）----
  async function applyShare() {
    const email = emailDraft.trim();
    if (!email) {
      setFormError(t('devices.batchShareEmailRequired'));
      return;
    }
    setBusy(true);
    setFormError(null);
    const out = await runBatch(manageableIds, async (id) => {
      await apiFetch(`/v3/devices/${encodeURIComponent(id)}/shares`, {
        method: 'POST',
        body: { email },
        idempotencyKey: crypto.randomUUID(),
      });
    });
    finish(out);
  }

  // ---- 改名（owner-only；前綴 + 編號，單台=前綴原樣）----
  async function applyRename() {
    const prefix = prefixDraft.trim();
    if (prefix.length < 1 || prefix.length > 32) {
      setFormError(t('devices.batchRenamePrefixError'));
      return;
    }
    setBusy(true);
    setFormError(null);
    const total = manageableIds.length;
    const out = await runBatch(manageableIds, async (id, i) => {
      await apiFetch(`/v3/devices/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: { display_name: batchRenameName(prefix, i, total) },
      });
    });
    finish(out);
  }

  // ---- 解綁（owner-only；逐台 Signal unbind）----
  async function applyDelete() {
    setBusy(true);
    setFormError(null);
    const out = await runBatch(manageableIds, async (id) => {
      try {
        await unbindDevice(id);
      } catch (e) {
        // 404 = 他處已解綁 → 等同成功（冪等）。
        if ((e as Error & { frame?: { code?: unknown } })?.frame?.code === 'device_not_found') return;
        throw e;
      }
    });
    finish(out);
  }

  const blockedNote =
    blockedIds.length > 0 ? t('devices.batchBlockedNote', { count: blockedIds.length }) : null;

  return (
    <div className="dv-batch-bar" role="region" aria-label={t('devices.batchBarLabel')} data-testid="batch-bar">
      <div className="dv-batch-row">
        <span className="dv-batch-count" role="status">
          {t('devices.batchSelectedCount', { count: selectedCount })}
        </span>
        <div className="dv-batch-actions">
          <button
            className="btn-ghost"
            data-testid="batch-move-room"
            disabled={busy || selectedCount === 0}
            onClick={() => {
              setOutcome(null);
              setMode(mode === 'move-room' ? null : 'move-room');
            }}
          >
            {t('devices.batchMoveRoom')}
          </button>
          <button
            className="btn-ghost"
            data-testid="batch-share"
            disabled={busy || !canManage}
            title={!canManage ? t('devices.batchNeedsOwner') : undefined}
            onClick={() => {
              setOutcome(null);
              setMode(mode === 'share' ? null : 'share');
            }}
          >
            {t('devices.batchShare')}
          </button>
          <button
            className="btn-ghost"
            data-testid="batch-rename"
            disabled={busy || !canManage}
            title={!canManage ? t('devices.batchNeedsOwner') : undefined}
            onClick={() => {
              setOutcome(null);
              setMode(mode === 'rename' ? null : 'rename');
            }}
          >
            {t('devices.batchRename')}
          </button>
          <button
            className="btn-danger"
            data-testid="batch-delete"
            disabled={busy || !canManage}
            title={!canManage ? t('devices.batchNeedsOwner') : undefined}
            onClick={() => {
              setOutcome(null);
              setConfirmDelete(true);
            }}
          >
            {t('devices.batchDelete')}
          </button>
          <button className="btn-ghost" data-testid="batch-exit" disabled={busy} onClick={onExit}>
            {t('common.done')}
          </button>
        </div>
      </div>

      {blockedNote && (
        <p className="dv-batch-note text-secondary" role="note">
          {blockedNote}
        </p>
      )}

      {mode === 'move-room' && (
        <div className="dv-batch-panel" data-testid="batch-move-room-panel">
          <label>
            {t('devices.batchMoveRoomLabel')}
            <input
              list="dv-batch-room-options"
              value={roomDraft}
              placeholder={t('devices.roomNamePlaceholder')}
              onChange={(e) => setRoomDraft(e.target.value)}
            />
          </label>
          <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
            {t('devices.batchMoveRoomHint')}
          </span>
          <div className="dv-batch-panel-actions">
            <button className="btn-primary" disabled={busy} onClick={applyMoveRoom} data-testid="batch-move-room-apply">
              {t('common.save')}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={closePanels}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {mode === 'share' && (
        <div className="dv-batch-panel" data-testid="batch-share-panel">
          <label>
            {t('devices.batchShareLabel', { count: manageableIds.length })}
            <input
              type="email"
              value={emailDraft}
              placeholder={t('sharing.emailPlaceholder')}
              onChange={(e) => setEmailDraft(e.target.value)}
              data-testid="batch-share-email"
            />
          </label>
          {formError && <p className="error-text" style={{ margin: 0 }}>{formError}</p>}
          <div className="dv-batch-panel-actions">
            <button className="btn-primary" disabled={busy} onClick={() => void applyShare()} data-testid="batch-share-apply">
              {busy ? t('sharing.sending') : t('sharing.sendInvite')}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={closePanels}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {mode === 'rename' && (
        <div className="dv-batch-panel" data-testid="batch-rename-panel">
          <label>
            {t('devices.batchRenameLabel', { count: manageableIds.length })}
            <input
              value={prefixDraft}
              maxLength={32}
              placeholder={t('devices.batchRenamePlaceholder')}
              onChange={(e) => setPrefixDraft(e.target.value)}
              data-testid="batch-rename-prefix"
            />
          </label>
          <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
            {manageableIds.length > 1
              ? t('devices.batchRenameHintMulti', { example: `${prefixDraft.trim() || t('devices.batchRenameExample')} 1` })
              : t('devices.batchRenameHintSingle')}
          </span>
          {formError && <p className="error-text" style={{ margin: 0 }}>{formError}</p>}
          <div className="dv-batch-panel-actions">
            <button className="btn-primary" disabled={busy} onClick={() => void applyRename()} data-testid="batch-rename-apply">
              {busy ? t('common.saving') : t('common.save')}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={closePanels}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {outcome && (
        <div className="dv-batch-result" role="status" data-testid="batch-result">
          <p style={{ margin: 0 }}>
            {t('devices.batchResultSummary', { ok: outcome.succeeded.length, fail: outcome.failed.length })}
          </p>
          {outcome.failed.length > 0 && (
            <ul className="dv-batch-fail-list">
              {outcome.failed.map((f) => (
                <li key={f.id} className="error-text">
                  {t('devices.batchResultFailRow', { name: nameOf.get(f.id) ?? f.id, error: f.error })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* 只讀 datalist（房間候選）供本列 move-room 的 input list 使用 */}
      <datalist id="dv-batch-room-options">
        {roomNames.map((r) => (
          <option key={r} value={r} />
        ))}
      </datalist>

      {confirmDelete && (
        <ConfirmDialog
          spec={{
            title: t('devices.batchDeleteConfirmTitle'),
            body: (
              <>
                <p style={{ marginTop: 0 }}>{t('devices.batchDeleteConfirmBody', { count: manageableIds.length })}</p>
                {blockedIds.length > 0 && <p style={{ marginBottom: 0 }}>{t('devices.batchBlockedNote', { count: blockedIds.length })}</p>}
              </>
            ),
            confirmLabel: t('devices.batchDelete'),
            danger: true,
            requireText: t('devices.batchDeleteConfirmWord'),
            requireTextHint: t('devices.batchDeleteConfirmHint'),
          }}
          busy={busy}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => void applyDelete()}
        />
      )}
    </div>
  );
}
